<template>
    <article v-if="match" class="match-card" :class="{ 'match-card--played': played, 'match-card--live': isLive }" @click="openMatch">
        <!-- always two lines: the competition with the date (or LIVE), then the round, so every card lines up -->
        <header class="match-card__meta">
            <span class="cl-chip cl-chip--gradient match-card__competition" :title="competition">{{ competition }}</span>
            <span v-if="isLive" class="match-card__live">
                <span class="match-card__live-dot" />LIVE
            </span>
            <span v-else-if="match.postponed" class="match-card__postponed">
                <v-icon x-small color="white">mdi-update</v-icon>Αναβολή
            </span>
            <span v-else class="match-card__date">
                <v-icon x-small>mdi-calendar-outline</v-icon>{{ getDate(match.matchDatetime) }}
            </span>
            <span class="match-card__round">{{ roundText }}</span>
        </header>

        <div class="match-card__body">
            <div class="match-card__team" @click.stop="goToTeam(match.team1)">
                <span class="cl-logo match-card__logo">
                    <img v-if="match.team1.logo" :src="`${assetsUrl}/${match.team1.logo}`" :alt="match.team1.name">
                    <v-icon v-else color="teal"> mdi-basketball-hoop </v-icon>
                </span>
                <span class="match-card__name" :class="{ 'match-card__name--winner': played && view.scoreTeam1 > view.scoreTeam2 }">{{ match.team1.name }}</span>
            </div>

            <!-- live: the score of the stat logs, as it moves -->
            <div v-if="isLive" class="match-card__score match-card__score--live">
                <span class="match-card__points--live">{{ view.scoreTeam1 || 0 }}</span>
                <span class="match-card__dash">-</span>
                <span class="match-card__points--live">{{ view.scoreTeam2 || 0 }}</span>
            </div>
            <div v-else-if="played" class="match-card__score">
                <span :class="view.scoreTeam1 > view.scoreTeam2 ? 'match-card__points--winner' : 'match-card__points'">{{ view.scoreTeam1 }}</span>
                <span class="match-card__dash">-</span>
                <span :class="view.scoreTeam2 > view.scoreTeam1 ? 'match-card__points--winner' : 'match-card__points'">{{ view.scoreTeam2 }}</span>
            </div>
            <div v-else class="match-card__upcoming">
                <span class="match-card__vs">VS</span>
                <span v-if="!match.postponed" class="match-card__time">{{ getTime(match.matchDatetime) }}</span>
            </div>

            <div class="match-card__team match-card__team--away" @click.stop="goToTeam(match.team2)">
                <span class="cl-logo match-card__logo">
                    <img v-if="match.team2.logo" :src="`${assetsUrl}/${match.team2.logo}`" :alt="match.team2.name">
                    <v-icon v-else color="teal"> mdi-basketball-hoop </v-icon>
                </span>
                <span class="match-card__name" :class="{ 'match-card__name--winner': played && view.scoreTeam2 > view.scoreTeam1 }">{{ match.team2.name }}</span>
            </div>
        </div>

        <footer class="match-card__footer">
            <button v-if="match.arena" type="button" class="match-card__arena" @click.stop="openMap(match.arena)">
                <v-icon x-small>mdi-map-marker-outline</v-icon>{{ match.arena.name }}
            </button>
            <v-spacer />
            <v-btn v-if="!match.pending && match.videoUrl" icon small title="YouTube" @click.stop="openVideo">
                <v-icon color="red">mdi-youtube</v-icon>
            </v-btn>
            <v-btn v-if="isLive || (!match.pending && !match.postponed)" icon small title="Match" @click.stop="openMatch">
                <v-icon class="match-card__go">mdi-arrow-right-circle-outline</v-icon>
            </v-btn>
        </footer>
    </article>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import { isMatchLive, refreshLiveStatuses, withLiveData, CHECK_AFTER_START_MINUTES, LIVE_TICK_MS } from '@/utils/matchLive'

    export default {
        props: {
            match: {
                type: Object,
                default: null
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                now: Date.now(),
                timer: null
            }
        },
        computed: {
            // the match with the score of the stat logs while it is live (same rules as the big site)
            view() {
                return withLiveData(this.match, this.now)
            },
            isLive() {
                return isMatchLive(this.match, this.now)
            },
            // a score to show: not pending, not postponed and not the 0-0 of a match not played yet
            played() {
                return !(this.view.pending || this.match.postponed || (this.view.scoreTeam1 === 0 && this.view.scoreTeam2 === 0))
            },
            competition() {
                if (this.match.cup) {
                    return this.match.cupLeague ? this.match.cupLeague.title : 'Κύπελλο'
                }
                return this.match.league ? this.match.league.title : 'Κύπελλο'
            },
            roundText() {
                const phase = this.match.cup ? 'Κύπελλο' : (this.match.phase === 'regular_season' ? 'Regular Season' : 'Play Offs')
                return `Matchday ${this.match.round} · ${phase}`
            }
        },
        created() {
            if (!this.match || this.match.postponed || !this.match.matchDatetime) {
                return
            }
            refreshLiveStatuses([this.match])
            // only matches still inside their live check window can change state
            const checkWindowEnd = momentTimezone(this.match.matchDatetime).valueOf() + CHECK_AFTER_START_MINUTES * 60 * 1000
            if (checkWindowEnd > Date.now()) {
                this.timer = setInterval(() => {
                    this.now = Date.now()
                    refreshLiveStatuses([this.match], this.now)
                }, LIVE_TICK_MS)
            }
        },
        beforeDestroy() {
            clearInterval(this.timer)
        },
        methods: {
            getDate(date) {
                return momentTimezone(date).tz('Europe/Athens').locale('el-GR').format('ddd D MMM YYYY')
            },
            getTime(date) {
                return momentTimezone(date).tz('Europe/Athens').format('HH:mm')
            },
            openMap(arena) {
                window.open(`https://www.google.com/maps/place/${arena.coordinates}`, '_self', 'noreferrer')
            },
            openVideo() {
                window.open(this.match.videoUrl.replace('embed/', 'watch?v='), '_self', 'noreferrer')
            },
            openMatch() {
                this.$router.push({ name: 'Match', params: { id: this.match.id } })
            },
            goToTeam(team) {
                this.$router.push({ name: 'Team', params: { id: team.id } })
            }
        }
    }
</script>

<style scoped>
.match-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    border-radius: 10px;
    background: #232323;
    color: #fff;
    box-shadow: 0 6px 20px rgba(35, 35, 35, 0.14);
    font-family: Roboto, Arial, sans-serif;
    text-align: left;
    cursor: pointer;
    overflow: hidden;
    transition: transform 0.15s, box-shadow 0.15s;
}

.match-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 26px rgba(3, 122, 222, 0.2);
}

.match-card__meta {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    column-gap: 10px;
    row-gap: 6px;
    padding: 12px 14px 0;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
}

.match-card__competition {
    display: block;
    justify-self: start;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.match-card__round {
    grid-column: 1 / -1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.match-card__date,
.match-card__postponed {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
}

.match-card__date .v-icon {
    color: #03e5b7 !important;
}

.match-card--live {
    box-shadow: 0 0 0 2px #ef4444, 0 10px 26px rgba(239, 68, 68, 0.25);
}

.match-card__live {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 9px;
    border-radius: 999px;
    background: #ef4444;
    color: #fff;
    font-weight: 800;
    letter-spacing: 0.1em;
}

.match-card__live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #fff;
    animation: match-card-live 1.2s ease-in-out infinite;
}

@keyframes match-card-live {
    50% {
        opacity: 0.25;
    }
}

.match-card__points--live {
    min-width: 54px;
    padding: 6px 8px;
    border-radius: 8px;
    background: rgba(239, 68, 68, 0.16);
    border: 1px solid rgba(239, 68, 68, 0.6);
    color: #fff;
    text-align: center;
}

.match-card__postponed {
    padding: 2px 8px;
    border-radius: 999px;
    background: #f44336;
    color: #fff;
    font-weight: 700;
}

.match-card__body {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: start;
    gap: 10px;
    flex: 1 0 auto;
    padding: 16px 14px;
}

/* the score / time sits at the height of the logos, whatever the length of the names */
.match-card__body > .match-card__score,
.match-card__body > .match-card__upcoming {
    height: 56px;
    justify-content: center;
}

.match-card__team {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    min-width: 0;
    text-align: center;
}

.match-card__logo {
    width: 56px;
    height: 56px;
}

.match-card__name {
    /* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix -- the line clamp needs the prefixed box */
    display: -webkit-box;
    -webkit-box-orient: vertical;
    /* stylelint-enable value-no-vendor-prefix, property-no-vendor-prefix */
    -webkit-line-clamp: 2;
    min-height: 2.4em;
    font-size: 0.9rem;
    font-weight: 600;
    line-height: 1.2;
    color: rgba(255, 255, 255, 0.8);
    overflow: hidden;
    overflow-wrap: break-word;
    hyphens: auto;
}

.match-card__name--winner {
    color: #fff;
    font-weight: 800;
}

.match-card__team:hover .match-card__name {
    color: #03e5b7;
}

.match-card__score {
    display: flex;
    align-items: center;
    gap: 4px;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 2.1rem;
    line-height: 1;
}

.match-card__points,
.match-card__points--winner {
    min-width: 54px;
    padding: 6px 8px;
    border-radius: 6px;
    text-align: center;
}

.match-card__points {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.7);
}

.match-card__points--winner {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    color: #fff;
}

.match-card__dash {
    color: rgba(255, 255, 255, 0.35);
}

.match-card__upcoming {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.match-card__vs {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.45);
}

.match-card__time {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 2rem;
    line-height: 1;
    color: #03e5b7;
}

.match-card__footer {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: auto;
    min-height: 41px;
    padding: 4px 8px 4px 14px;
    border-top: 1px solid rgba(255, 255, 255, 0.07);
    background: rgba(255, 255, 255, 0.02);
}

.match-card__arena {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    color: rgba(255, 255, 255, 0.65);
    font-size: 0.78rem;
    text-align: left;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.match-card__arena .v-icon {
    color: #03e5b7 !important;
}

.match-card__arena:hover {
    color: #fff;
}

.match-card__go {
    color: #03e5b7 !important;
}
</style>
